import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Check, AlertTriangle, ChevronDown, Quote, Clock, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { StrategicOption } from "./StrategicOptionCard";
import { archetypeConfig } from "@/lib/archetypeConfig";

interface StrategicOptionCardCompactProps {
  option: StrategicOption;
  optionLabel: string;
  className?: string;
  style?: React.CSSProperties;
}

// Ein-Satz-Wahrheiten pro Archetyp - schmerzhaft ehrlich
const archetypeTruth: Record<string, string> = {
  low_risk: "Wir akzeptieren, dass Quentari außerhalb des Kernsegments unsichtbar bleibt.",
  value_max: "Wir akzeptieren Unschärfe, um Reichweite zu gewinnen.",
  scaling: "Diese Option schützt vor falschen Entscheidungen, indem sie gar keine trifft."
};

export const StrategicOptionCardCompact = ({ option, optionLabel, className, style }: StrategicOptionCardCompactProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMoreBullets, setShowMoreBullets] = useState(false);
  const config = archetypeConfig[option.archetype] || archetypeConfig.low_risk;
  const Icon = config.icon;
  const isScaling = option.archetype === "scaling";
  const truth = archetypeTruth[option.archetype] || "";

  // Get first advantage, risk, and strategic goal as quick bullets
  const quickBullets = [
    { type: 'goal', text: option.strategic_goal },
    { type: 'plus', text: option.key_advantages[0] },
    { type: 'minus', text: option.key_risks[0] }
  ].filter(b => b.text);

  // Additional advantages and risks (beyond the first one)
  const additionalAdvantages = option.key_advantages.slice(1);
  const additionalRisks = option.key_risks.slice(1);
  const hasMoreBullets = additionalAdvantages.length > 0 || additionalRisks.length > 0;

  return (
    <Card 
      className={cn(
        "relative overflow-hidden border-l-4 transition-all duration-300",
        config.headerColor,
        isOpen && "ring-2 ring-primary/30 shadow-lg shadow-primary/5 scale-[1.01]",
        !isOpen && "hover:shadow-md hover:border-l-[6px]",
        // Option C (scaling) visuell abgesetzt
        isScaling && "opacity-80 border-dashed",
        className
      )} 
      style={style}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className={cn(
            "w-full text-left p-5 transition-colors",
            !isOpen && "hover:bg-muted/30"
          )}>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-3 flex-1 min-w-0">
                {/* Option Label + Archetype */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-bold text-foreground">{optionLabel}</span>
                  <Badge 
                    variant="outline" 
                    className={cn("px-2 py-0.5 text-xs font-medium border gap-1", config.color)}
                  >
                    <Icon className="h-3 w-3" />
                    <span>{config.label}</span>
                  </Badge>
                  {/* Scaling-Warnung */}
                  {isScaling && (
                    <Badge variant="outline" className="px-2 py-0.5 text-xs border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 gap-1">
                      <Clock className="h-3 w-3" />
                      Keine Marktvalidierung
                    </Badge>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold tracking-tight leading-snug pr-8">
                  {option.name}
                </h3>

                {/* Ein-Satz-Wahrheit - immer sichtbar */}
                <p className={cn(
                  "text-sm font-medium italic",
                  isScaling ? "text-amber-700 dark:text-amber-400" : "text-primary"
                )}>
                  „{truth}"
                </p>

                {/* Quick Bullets - Always visible, full text readable */}
                <div className="space-y-2">
                  {quickBullets.map((bullet, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      {bullet.type === 'goal' && (
                        <span className={cn("mt-1.5 h-1.5 w-1.5 rounded-full shrink-0", config.dotColor)} />
                      )}
                      {bullet.type === 'plus' && (
                        <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                      )}
                      {bullet.type === 'minus' && (
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />
                      )}
                      <span className="text-muted-foreground leading-snug">{bullet.text}</span>
                    </div>
                  ))}

                  {/* Additional bullets - expandable */}
                  {showMoreBullets && (
                    <div className="space-y-2 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
                      {additionalAdvantages.map((adv, i) => (
                        <div key={`adv-${i}`} className="flex items-start gap-2 text-sm">
                          <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                          <span className="text-muted-foreground leading-snug">{adv}</span>
                        </div>
                      ))}
                      {additionalRisks.map((risk, i) => (
                        <div key={`risk-${i}`} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />
                          <span className="text-muted-foreground leading-snug">{risk}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show more button */}
                  {hasMoreBullets && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-xs text-primary hover:text-primary/80 hover:bg-transparent font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMoreBullets(!showMoreBullets);
                      }}
                    >
                      <ChevronRight className={cn(
                        "h-3 w-3 mr-1 transition-transform duration-200",
                        showMoreBullets && "rotate-90"
                      )} />
                      {showMoreBullets 
                        ? "Weniger anzeigen" 
                        : `+${additionalAdvantages.length + additionalRisks.length} weitere`}
                    </Button>
                  )}
                </div>
              </div>

              {/* Expand indicator */}
              <div className="shrink-0 mt-1">
                <ChevronDown 
                  className={cn(
                    "h-5 w-5 text-muted-foreground transition-transform duration-200",
                    isOpen && "rotate-180"
                  )} 
                />
              </div>
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="animate-accordion-down">
          <CardContent className="pt-0 pb-5 px-5 space-y-4 border-t border-border/50 bg-gradient-to-b from-primary/[0.02] to-transparent">
            {/* Scaling-Warnung im Detail - brutal ehrlich */}
            {isScaling && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mt-4">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">
                    Diese Option ist keine Strategie – sie ist ein Aufschub.
                  </p>
                  <p className="text-sm text-destructive/80 mt-1">
                    Du gewinnst Zeit, ohne dich festzulegen – auf Kosten realer Marktrückmeldung.
                  </p>
                </div>
              </div>
            )}

            {/* Description */}
            <p className={cn("text-sm text-muted-foreground", !isScaling && "pt-4")}>
              {option.description}
            </p>

            {/* Context Reference */}
            {option.user_context_reference && (
              <div className="rounded-lg p-3 bg-primary/5 border border-primary/10">
                <div className="flex items-start gap-2">
                  <Quote className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-primary mb-1">Bezug zu deiner Kernhypothese</p>
                    <p className="text-sm text-foreground/90">{option.user_context_reference}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Full Advantages & Risks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                  Was du gewinnst
                </p>
                <ul className="space-y-1.5">
                  {option.key_advantages.map((adv, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span>{adv}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-destructive uppercase tracking-wide">
                  Was du aufgibst
                </p>
                <ul className="space-y-1.5">
                  {option.key_risks.map((risk, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                      <span>{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Differentiation */}
            <div className="pt-3 border-t border-border/50">
              <p className="text-xs font-medium text-primary mb-1">Der echte Preis</p>
              <p className="text-sm text-foreground/80">{option.differentiation_factor}</p>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};